import type { SupabaseClient } from '@supabase/supabase-js'
import type { Servicio, Database } from '@/types/database'
import { fetchAllPages, fetchInChunks } from '@/lib/postgrest-chunks'

type ServicioInsert = Database['public']['Tables']['servicios']['Insert']
type ServicioUpdate = Database['public']['Tables']['servicios']['Update']
type ClienteServicioPrecioRow = Database['public']['Tables']['cliente_servicio_precios']['Row']
type ClienteServicioPrecioInsert = Database['public']['Tables']['cliente_servicio_precios']['Insert']
type ClienteServicioPrecioUpdate = Database['public']['Tables']['cliente_servicio_precios']['Update']

export interface ServicioConClientes extends Servicio {
  clientesCount: number
}

// Re-export para compatibilidad
export type ClienteServicioPrecio = ClienteServicioPrecioRow

// Columnas de servicios sin precio_estimado ni precio_base. Los precios se leen de vista_precios_servicios.
const COLUMNAS_SIN_PRECIO =
  'id, organizacion_id, nombre, codigo, descripcion, categoria, estado, activo, periodicidad, frecuencia, moneda, tipo_facturacion, aplica_persona_fisica, aplica_persona_juridica, aplica_sucesion_indivisa, created_at, updated_at'

// Columnas de cliente_servicio_precios sin el precio. El precio se lee de vista_precios_servicios_cliente.
export const COLUMNAS_ASIGNACION_SIN_PRECIO =
  'id, cliente_id, servicio_id, activo, fecha_desde, fecha_hasta, frecuencia, mes_inicio, observaciones, pagador_id, created_at, updated_at'

/**
 * Precio de cada fila de cliente_servicio_precios, por id, leído de vista_precios_servicios_cliente.
 * Sin verHonorariosCliente en la organización del cliente, el precio llega null.
 */
export const preciosDeAsignaciones = async (
  client: SupabaseClient<Database>,
  ids: string[],
): Promise<Map<string, number | null>> => {
  const filas = await fetchInChunks(ids, async lote => {
    const { data, error } = await client
      .from('vista_precios_servicios_cliente')
      .select('id, precio')
      .in('id', lote)

    if (error) throw error
    return data ?? []
  })
  return new Map(filas.flatMap(f => (f.id ? [[f.id, f.precio] as const] : [])))
}

/**
 * precio_estimado y precio_base del catálogo, por id de servicio, leídos de vista_precios_servicios.
 * La vista solo devuelve los servicios de las organizaciones donde el caller tiene verHonorariosCliente.
 */
const preciosDelCatalogo = async (
  client: SupabaseClient<Database>,
  ids: string[],
): Promise<Map<string, Pick<Servicio, 'precio_estimado' | 'precio_base'>>> => {
  const data = await fetchInChunks(ids, async lote => {
    const { data, error } = await client
      .from('vista_precios_servicios')
      .select('id, precio_estimado, precio_base')
      .in('id', lote)

    if (error) throw error
    return data ?? []
  })
  return new Map(data.flatMap(p => (p.id
    ? [[p.id, { precio_estimado: p.precio_estimado, precio_base: p.precio_base }] as const]
    : [])))
}

/**
 * Servicio para gestión de servicios.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const servicios = await serviciosService.listar(supabase!, organizacionId, puede.verHonorariosCliente)
 */
export const serviciosService = {
  async listar(client: SupabaseClient<Database>, organizacionId: string, incluirPrecio: boolean): Promise<ServicioConClientes[]> {
    // Obtener servicios
    const { data: servicios, error } = await client
      .from('servicios')
      .select(COLUMNAS_SIN_PRECIO)
      .eq('organizacion_id', organizacionId)
      .order('nombre')

    if (error) throw error

    const precios = incluirPrecio ? await preciosDelCatalogo(client, (servicios ?? []).map(s => s.id)) : null

    // Clientes activos por servicio. cliente_servicio_precios no tiene organizacion_id:
    // se acota por la del servicio con un join, para no bajar asignaciones de otras organizaciones.
    // Se lee por páginas ordenadas por PK, porque PostgREST corta cada respuesta en 1000 filas.
    const csData = await fetchAllPages<{ servicio_id: string }>(async (desde, hasta) => {
      const { data, error } = await client
        .from('cliente_servicio_precios')
        .select('servicio_id, servicio:servicios!servicio_id!inner(organizacion_id)')
        .eq('activo', true)
        .eq('servicio.organizacion_id', organizacionId)
        .order('id')
        .range(desde, hasta)

      if (error) throw error
      return (data ?? []) as { servicio_id: string }[]
    })

    // Contar clientes por servicio
    const conteo: Record<string, number> = {}
    csData.forEach(cs => {
      conteo[cs.servicio_id] = (conteo[cs.servicio_id] || 0) + 1
    })

    const serviciosData = (servicios || []) as Servicio[]
    return serviciosData.map(s => ({
      ...s,
      ...precios?.get(s.id),
      clientesCount: conteo[s.id] || 0,
    })) as ServicioConClientes[]
  },

  async obtenerPorId(client: SupabaseClient<Database>, id: string, incluirPrecio: boolean): Promise<Servicio> {
    const { data, error } = await client
      .from('servicios')
      .select(COLUMNAS_SIN_PRECIO)
      .eq('id', id)
      .single()

    if (error) throw error
    const precios = incluirPrecio ? await preciosDelCatalogo(client, [id]) : null
    return { ...data, ...precios?.get(id) } as Servicio
  },

  async crear(client: SupabaseClient<Database>, servicio: ServicioInsert): Promise<Servicio> {
    const { data, error } = await client
      .from('servicios')
      .insert(servicio)
      .select(COLUMNAS_SIN_PRECIO)
      .single()

    if (error) throw error
    return data as Servicio
  },

  async actualizar(client: SupabaseClient<Database>, id: string, servicio: ServicioUpdate): Promise<Servicio> {
    const { data, error } = await client
      .from('servicios')
      .update(servicio)
      .eq('id', id)
      .select(COLUMNAS_SIN_PRECIO)
      .single()

    if (error) throw error
    return data as Servicio
  },

  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_servicio_seguro', { p_id: id })
    if (error) throw error
  },

  async toggleEstado(client: SupabaseClient<Database>, id: string, nuevoEstado: 'activo' | 'inactivo'): Promise<Servicio> {
    const updateData: ServicioUpdate = {
      estado: nuevoEstado,
    }
    const { data, error } = await client
      .from('servicios')
      .update(updateData)
      .eq('id', id)
      .select(COLUMNAS_SIN_PRECIO)
      .single()

    if (error) throw error
    return data as Servicio
  },

  // Obtener clientes que tienen un servicio específico
  async obtenerClientesDelServicio(client: SupabaseClient<Database>, servicioId: string): Promise<{ id: string; nombre: string; precio: number }[]> {
    const { data, error } = await client
      .from('cliente_servicio_precios')
      .select(`
        id,
        cliente_id,
        clientes!inner(
          id,
          personas!inner(nombre_completo)
        )
      `)
      .eq('servicio_id', servicioId)
      .eq('activo', true)

    if (error) throw error

    const filas = (data || []) as {
      id: string
      cliente_id: string
      clientes: { id: string; personas: { nombre_completo: string | null } }
    }[]
    const precios = await preciosDeAsignaciones(client, filas.map(cs => cs.id))
    return filas.map(cs => ({
      id: cs.clientes?.id || '',
      nombre: cs.clientes?.personas?.nombre_completo || '',
      precio: precios.get(cs.id) || 0,
    })).filter(c => c.id)
  },

  // Servicios activos de un cliente con su precio. Sin verHonorariosCliente el precio llega en 0.
  async obtenerServiciosCliente(client: SupabaseClient<Database>, clienteV2Id: string): Promise<(ClienteServicioPrecio & { servicio_nombre: string })[]> {
    const { data, error } = await client
      .from('cliente_servicio_precios')
      .select(`${COLUMNAS_ASIGNACION_SIN_PRECIO}, servicios(nombre)`)
      .eq('cliente_id', clienteV2Id)
      .eq('activo', true)
      .order('created_at')

    if (error) throw error

    const filas = (data || []) as unknown as (Omit<ClienteServicioPrecio, 'precio'> & { servicios: { nombre: string } | null })[]
    const precios = await preciosDeAsignaciones(client, filas.map(item => item.id))
    return filas.map(item => ({
      ...item,
      precio: precios.get(item.id) ?? 0,
      servicio_nombre: item.servicios?.nombre || '',
    }))
  },

  // Asignar servicio a cliente con precio
  async asignarServicioCliente(client: SupabaseClient<Database>, data: {
    cliente_id: string
    servicio_id: string
    precio: number
    frecuencia?: string
    mes_inicio?: number
  }): Promise<ClienteServicioPrecio> {
    const insertData: ClienteServicioPrecioInsert = {
      cliente_id: data.cliente_id,
      servicio_id: data.servicio_id,
      precio: data.precio,
      frecuencia: data.frecuencia ?? 'mensual',
      mes_inicio: data.mes_inicio ?? 1,
    }
    const { data: result, error } = await client
      .from('cliente_servicio_precios')
      .insert(insertData)
      .select(COLUMNAS_ASIGNACION_SIN_PRECIO)
      .single()

    if (error) throw error
    return { ...result, precio: data.precio }
  },

  // Desactivar servicio de cliente
  async desactivarServicioCliente(client: SupabaseClient<Database>, id: string): Promise<void> {
    const updateData: ClienteServicioPrecioUpdate = {
      activo: false,
      fecha_hasta: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString(),
    }
    const { error } = await client
      .from('cliente_servicio_precios')
      .update(updateData)
      .eq('id', id)

    if (error) throw error
  },

  // Eliminar servicios activos de un cliente (para reemplazo completo)
  async eliminarServiciosActivosCliente(client: SupabaseClient<Database>, clienteId: string): Promise<void> {
    const { error } = await client
      .from('cliente_servicio_precios')
      .delete()
      .eq('cliente_id', clienteId)
      .eq('activo', true)

    if (error) throw error
  },
}
